import React, { useEffect, useState } from 'react';
import { EditOutlined } from '@ant-design/icons';
import { Badge, Popconfirm } from 'antd';

import { useHrTakeMutation, useLazyGetAllReferencesQuery } from 'app/services/edpApiV1/references';

import { viewerModel } from 'entities/viewer';

import { dataForReferencesTable, sortingDate } from 'shared/helpers/utils';
import { useNotifications } from 'shared/hooks/use-notifications';
import { RequestsSearch, TColumns } from 'shared/types/types';

import { TablePull } from 'features/tables';
import { usePullSort } from 'shared/hooks';
import { RolesUser, SortCondition } from 'shared/enums';
import { useAppSelector } from 'app/store';
import { ONE_MINUTE } from 'shared/constants';
import { useIndicators } from 'features/indicators';
import { TPreparedReferencesTableData } from 'shared/types/reference.types';
import { SearchKeysNews } from 'shared/enums/news';
import { getAllAvailableOrganizations } from 'entities/organizations/model/organizations';
import ReferencesModalVacationsSubordinates from '../referencesModalSubordinates';
import style from './index.module.scss';

const ReferencesPull = () => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentSize, setCurrentSize] = useState<number>(10);
  const [dataReferences, setDataReferences] = useState<TPreparedReferencesTableData[]>();
  const [requestId, setRequestId] = useState<number>();
  const [openModal, setOpenModal] = useState(false);
  const [sortInfo, trigger] = usePullSort();
  const [search, setSearch] = useState<RequestsSearch>({ byId: undefined, fio: undefined });
  const allAvailableOrganizations = useAppSelector(getAllAvailableOrganizations);

  const { role } = useAppSelector(viewerModel.me.selectors.selectViewerProfile);

  const [hrTakeTrigger, resultHrToTake] = useHrTakeMutation();
  // Добавляем пуллинг обновления списка и обновляем индикаторы через хук useIndicators
  const [getAllReferencesTriger, resultAllReferences] = useLazyGetAllReferencesQuery({ pollingInterval: ONE_MINUTE });
  const { data } = resultAllReferences;

  const { isSuccess: successHrToTake, isError: errorHrToTake, reset: resetHrToTake } = resultHrToTake;

  const handleToWork = (id: string | number): void => {
    hrTakeTrigger({ requestId: Number(id), allAvailableOrganizations });
  };

  const handleChangeResponsible = (id: number): void => {
    setRequestId(id);
    setOpenModal(true);
  };

  const columns: TColumns = [
    {
      title: '№',
      dataIndex: 'id',
      key: 'key',
      width: 120,
    },
    {
      title: 'Дата',
      dataIndex: 'created',
      key: 'created',
      sortDirections: [SortCondition.ASCEND, SortCondition.DESCEND],
      sorter: (a, b) => sortingDate(a.created, b.created),
      onHeaderCell: () => ({
        onClick: () => trigger(),
      }),
    },
    {
      title: 'Вид справки',
      dataIndex: 'refTypeName',
      key: 'refTypeName',
    },
    {
      title: 'Период',
      dataIndex: 'year',
      key: 'year',
    },
    {
      title: 'ФИО',
      dataIndex: 'fio',
      key: 'fio',
    },
    {
      title: 'Получение',
      dataIndex: 'gettingMethod',
      key: 'gettingMethod',
    },
    {
      title: 'Действие',
      dataIndex: 'actions',
      key: 'actions',
      render: (_, record: { key: React.Key }) => (
        <Popconfirm placement="leftTop" title="Вы уверены?" onConfirm={() => handleToWork(record.key)}>
          <a>Взять в работу</a>
        </Popconfirm>
      ),
    },
  ];

  const columnsWithSubordinates: TColumns = [
    {
      title: '№',
      dataIndex: 'id',
      key: 'key',
      width: 120,
    },
    {
      title: 'Дата',
      dataIndex: 'created',
      key: 'created',
      sortDirections: [SortCondition.ASCEND, SortCondition.DESCEND],
      sorter: (a, b) => sortingDate(a.created, b.created),
      onHeaderCell: () => ({
        onClick: () => trigger(),
      }),
    },
    {
      title: 'Вид справки',
      dataIndex: 'refTypeName',
      key: 'refTypeName',
    },
    {
      title: 'Период',
      dataIndex: 'year',
      key: 'year',
    },
    {
      title: 'ФИО',
      dataIndex: 'fio',
      key: 'fio',
    },
    {
      title: 'Получение',
      dataIndex: 'gettingMethod',
      key: 'gettingMethod',
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: status => (
        <span className="d-flex">
          <Badge status={status.type} />
          {status.text}
        </span>
      ),
    },
    {
      title: 'Действие',
      dataIndex: 'actions',
      key: 'actions',
      render: (_, record: { key: React.Key }) => (
        <Popconfirm placement="leftTop" title="Вы уверены?" onConfirm={() => handleToWork(record.key)}>
          <a>Взять в работу</a>
        </Popconfirm>
      ),
    },
  ];

  if (!allAvailableOrganizations && role === RolesUser.HRDirector) {
    columnsWithSubordinates.push({
      title: 'Ответственный',
      dataIndex: 'hr',
      key: 'hr',
      render: (hr, record: { key: React.Key }) => (
        <>
          {hr && (hr.name || hr.middleName || hr.lastName) ? (
            <>
              <span>{`${hr.lastName} ${hr.name} ${hr.middleName ?? ''}`}</span>
              <EditOutlined
                className={style.btnChangeResponsible}
                onClick={() => handleChangeResponsible(Number(record.key))}
              />
            </>
          ) : (
            <span className={style.changeResponsible} onClick={() => handleChangeResponsible(Number(record.key))}>
              Назначить
            </span>
          )}
        </>
      ),
    });
  }

  const onSearch = (data: string, key: SearchKeysNews) => {
    if (key === SearchKeysNews.SEARCH_ID) {
      setSearch({ fio: undefined, byId: data ? data : undefined });
    } else if (key === SearchKeysNews.SEARCH_NAME) {
      setSearch({ byId: undefined, fio: data ? data : undefined });
    } else {
      setSearch({ [key]: data ? data : undefined });
    }
  };

  useEffect(() => {
    if (role === RolesUser.Consultant) {
      if (sortInfo.value === null)
        getAllReferencesTriger({
          type: 'new',
          page: currentPage,
          size: currentSize,
          allAvailableOrganizations,
        });
      if (sortInfo.value === SortCondition.ASCEND)
        getAllReferencesTriger({
          type: 'new',
          page: currentPage,
          size: currentSize,
          order: SortCondition.ASC,
          allAvailableOrganizations,
          ...search,
        });
      if (sortInfo.value === SortCondition.DESCEND)
        getAllReferencesTriger({
          type: 'new',
          page: currentPage,
          size: currentSize,
          order: SortCondition.DESC,
          allAvailableOrganizations,
          ...search,
        });
    } else {
      if (sortInfo.value === null)
        getAllReferencesTriger({
          type: 'new',
          page: currentPage,
          size: currentSize,
          ...search,
          allAvailableOrganizations,
        });
      if (sortInfo.value === SortCondition.ASCEND)
        getAllReferencesTriger({
          type: 'new',
          page: currentPage,
          size: currentSize,
          order: SortCondition.ASC,
          allAvailableOrganizations,
          ...search,
        });
      if (sortInfo.value === SortCondition.DESCEND)
        getAllReferencesTriger({
          type: 'new',
          page: currentPage,
          size: currentSize,
          order: SortCondition.DESC,
          allAvailableOrganizations,
          ...search,
        });
    }
  }, [role, currentPage, getAllReferencesTriger, sortInfo.value, search, currentSize, allAvailableOrganizations]);

  useEffect(() => {
    if (data) {
      const dataTable = dataForReferencesTable(data?.data);
      if (dataTable) {
        setDataReferences(dataTable);
      }
    }
  }, [data]);

  useEffect(() => {
    const paginationInfo = data?.pagination;
    if (dataReferences?.length === 0 && paginationInfo && currentPage !== 1) {
      // определяю текущую страницу
      const totalPages = Math.ceil(paginationInfo.total / paginationInfo.size);
      //  устанавливает текущую страницу на минимальное значение из текущей страницы и общего количества страниц,
      //  чтобы избежать установки текущей страницы на значение, превышающее общее количество страниц.
      setCurrentPage(Math.min(currentPage, totalPages));
    }
  }, [dataReferences, data]);

  // подписываемся на обновление индикаторов, после обновления списка  и отключаем пуллинг звпроса индикаторов
  useIndicators(resultAllReferences.isSuccess, resultAllReferences.isFetching, false);

  useNotifications(successHrToTake, errorHrToTake, 'Заявка взята в работу', 'Что-то пошло не так', resetHrToTake);

  return (
    <>
      {dataReferences && (
        <TablePull
          dataSource={dataReferences}
          columns={role === RolesUser.HRDirector || role === RolesUser.SecondLine ? columnsWithSubordinates : columns}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          currentSize={currentSize}
          setCurrentSize={setCurrentSize}
          total={data?.pagination?.total}
          onSearch={onSearch}
        />
      )}
      {requestId && (
        <ReferencesModalVacationsSubordinates openModal={openModal} setOpenModal={setOpenModal} requestId={requestId} />
      )}
    </>
  );
};

export default ReferencesPull;
